import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// Sign out after a few minutes without activity, every role (transcripts are data involving minors,
// and coders may work on shared computers). A minute before, a dialog says when; Stay signed in
// keeps the session. The last activity is kept in localStorage, so every open ADAS tab shares one
// timer: working in one tab keeps the others signed in, and they all sign out together.
export const IDLE_TIMEOUT_MS = (Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 5) * 60 * 1000;
export const WARNING_MS = 60 * 1000;
export const LAST_ACTIVITY_KEY = 'adas.lastActivity';

const ACTIVITY_EVENTS = ['pointerdown', 'mousemove', 'keydown', 'wheel', 'touchstart', 'scroll'];
// Activity is written at most this often; the timer only needs it to the second or so
const WRITE_EVERY_MS = 5 * 1000;
// While there is activity, tell the backend at most this often, so a stretch of reading with no
// API call does not look like a closed tab (Admin ▸ Activity sessions, YES-314)
export const HEARTBEAT_MS = 60 * 1000;

const readLastActivity = () => Number(window.localStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
const writeLastActivity = (at = Date.now()) => window.localStorage.setItem(LAST_ACTIVITY_KEY, String(at));

const formatCountdown = (ms) => {
    const seconds = Math.max(0, Math.ceil(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const IdleSignOut = () => {
    const { isAuthenticated, logout } = useAuth();
    const location = useLocation();
    const [remaining, setRemaining] = useState(null);
    const lastWrite = useRef(0);
    const lastBeat = useRef(Date.now());
    const signingOut = useRef(false);
    // AuthContext's logout is a new function on every render; the timer must not restart for it
    const logoutRef = useRef(logout);
    logoutRef.current = logout;
    const warning = remaining !== null;
    // The page to come back to after signing in again, read when the timer runs out
    const here = useRef('');
    here.current = `${location.pathname}${location.search}${location.hash}`;

    const stay = useCallback(() => {
        writeLastActivity();
        lastWrite.current = Date.now();
        setRemaining(null);
    }, []);

    // A new session starts the clock: an old value from an earlier session never counts
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        signingOut.current = false;
        stay();
        return undefined;
    }, [isAuthenticated, stay]);

    // Activity resets the timer, except while the warning is up: then only Stay signed in does
    useEffect(() => {
        if (!isAuthenticated || warning) return undefined;
        const onActivity = () => {
            const now = Date.now();
            if (now - lastWrite.current < WRITE_EVERY_MS) return;
            lastWrite.current = now;
            writeLastActivity(now);
        };
        ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true, capture: true }));
        return () => ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
    }, [isAuthenticated, warning]);

    // Once a second (and when the tab comes back into view, since hidden tabs run timers late),
    // compare the last activity in any tab with the timeout
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const check = () => {
            if (signingOut.current) return;
            const left = IDLE_TIMEOUT_MS - (Date.now() - readLastActivity());
            if (left <= 0) {
                signingOut.current = true;
                setRemaining(null);
                const back = encodeURIComponent(window.location.origin + here.current);
                logoutRef.current({ redirectUrl: `/login?reason=idle&redirect_url=${back}`, cause: 'idle' });
            } else {
                setRemaining(left <= WARNING_MS ? left : null);
                const now = Date.now();
                if (now - lastBeat.current >= HEARTBEAT_MS && readLastActivity() > lastBeat.current) {
                    lastBeat.current = now;
                    api.post('/api/sessions/heartbeat').catch(() => {});
                }
            }
        };
        check();
        const timer = window.setInterval(check, 1000);
        document.addEventListener('visibilitychange', check);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', check);
        };
    }, [isAuthenticated]);

    return (
        <Dialog open={isAuthenticated && warning} onClose={stay} aria-labelledby="idle-title" aria-describedby="idle-text" maxWidth="xs" fullWidth>
            <DialogTitle id="idle-title" sx={{ typography: 'h5', pb: 1 }}>Are you still there?</DialogTitle>
            <DialogContent id="idle-text" sx={{ color: 'text.secondary' }}>
                To protect the classroom data in ADAS, you'll be signed out in{' '}
                <strong>{formatCountdown(remaining)}</strong> because there has been no activity. Anything not yet saved on this page will be lost.
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="contained" onClick={stay} autoFocus>Stay signed in</Button>
            </DialogActions>
        </Dialog>
    );
};

export default IdleSignOut;
